import { useCallback, useEffect, useState } from 'react';
import { Check, Copy, Inbox, KeyRound, Plus, RefreshCw, ShieldOff } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { api } from '../../api';
import { EmptyState, PageHeader, StatusBadge, Surface } from '../../components/ui';

function tokenStatus(token, t) {
  if (token.is_revoked) return { label: t('adminTokens.statusRevoked'), tone: 'muted' };
  if (token.is_used) return { label: t('adminTokens.statusUsed'), tone: 'accent' };
  if (token.expires_at && new Date(token.expires_at) < new Date()) return { label: t('adminTokens.statusExpired'), tone: 'warning' };
  return { label: t('adminTokens.statusActive'), tone: 'success' };
}

export default function TokensPage() {
  const { t } = useTranslation();
  const [tokens, setTokens] = useState([]);
  const [note, setNote] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [revoking, setRevoking] = useState(null);
  const [copied, setCopied] = useState(null);
  const [error, setError] = useState('');

  const load = useCallback(() => {
    setLoading(true); setError('');
    api.getTokens().then(setTokens).catch((err) => setError(err.message)).finally(() => setLoading(false));
  }, []);
  useEffect(load, [load]);

  const handleCreate = async (event) => {
    event.preventDefault(); setCreating(true); setError('');
    try { await api.createToken({ note: note || undefined, expires_at: expiresAt || undefined }); setNote(''); setExpiresAt(''); load(); }
    catch (err) { setError(err.message); }
    finally { setCreating(false); }
  };
  const handleRevoke = async (id) => {
    if (!window.confirm(t('adminTokens.confirmRevoke'))) return;
    setRevoking(id); setError('');
    try { await api.revokeToken(id); load(); }
    catch (err) { setError(err.message); }
    finally { setRevoking(null); }
  };
  const copy = (token) => navigator.clipboard.writeText(token.token).then(() => { setCopied(token.id); setTimeout(() => setCopied(null), 2000); });
  const activeCount = tokens.filter((token) => !token.is_revoked && !token.is_used && (!token.expires_at || new Date(token.expires_at) > new Date())).length;

  return (
    <div className="page">
      <PageHeader icon={KeyRound} title={t('adminTokens.title')} subtitle={t('adminTokens.subtitle')} />
      {error && <div className="alert alert-danger" role="alert">{error}</div>}
      <div className="content-grid">
        <Surface className="grid-span-4" title={t('adminTokens.createTitle')} icon={Plus}>
          <div className="surface-body">
            <form onSubmit={handleCreate}>
              <div className="mb-3"><label className="form-label" htmlFor="token-note">{t('adminTokens.noteLabel')}</label><input id="token-note" className="form-control" value={note} onChange={(event) => setNote(event.target.value)} placeholder={t('adminTokens.notePlaceholder')} /></div>
              <div className="mb-3"><label className="form-label" htmlFor="token-expiry">{t('adminTokens.expiresLabel')}</label><input id="token-expiry" className="form-control" type="datetime-local" value={expiresAt} onChange={(event) => setExpiresAt(event.target.value)} /><div className="form-text">{t('adminTokens.expiresHint')}</div></div>
              <button type="submit" className="btn btn-primary w-100" disabled={creating}><Plus className="icon" aria-hidden="true" />{creating ? t('adminTokens.creating') : t('adminTokens.createBtn')}</button>
            </form>
            <div className="alert alert-info mt-3 mb-0"><strong>{t('adminTokens.howToUse')}</strong><br />{t('adminTokens.howToUseText')}</div>
          </div>
        </Surface>

        <Surface className="grid-span-8" title={activeCount > 0 ? t('adminTokens.activeOf', { active: activeCount, total: tokens.length }) : t('adminTokens.totalCount', { total: tokens.length })} action={
          <button type="button" className="icon-btn" onClick={load} title={t('adminDevices.refresh')} aria-label={t('adminDevices.refresh')}><RefreshCw className="icon" aria-hidden="true" /></button>
        }>
          {loading ? <div className="empty-state"><div className="spinner-border spinner-border-sm" role="status" /></div>
            : tokens.length === 0 ? <EmptyState icon={Inbox} title={t('adminTokens.noTokens')} />
              : <div className="table-responsive"><table className="table table-hover mb-0">
                <thead><tr>{[t('adminTokens.colToken'), t('adminTokens.colStatus'), t('adminTokens.colNote'), t('adminTokens.colExpires'), t('adminTokens.colCreated'), ''].map((heading, index) => <th key={`${heading}-${index}`}>{heading}</th>)}</tr></thead>
                <tbody>{tokens.map((token) => {
                  const status = tokenStatus(token, t);
                  const canRevoke = !token.is_revoked && !token.is_used;
                  return <tr key={token.id}>
                    <td><div className="d-flex align-items-center gap-2"><code>{token.token.slice(0, 14)}…</code><button type="button" className="icon-btn" onClick={() => copy(token)} title={t('adminTokens.copyToken')} aria-label={t('adminTokens.copyToken')}>{copied === token.id ? <Check className="icon text-success" aria-hidden="true" /> : <Copy className="icon" aria-hidden="true" />}</button></div></td>
                    <td><StatusBadge tone={status.tone}>{status.label}</StatusBadge></td>
                    <td className="small text-muted">{token.note || '—'}</td>
                    <td className="small text-muted">{token.expires_at ? new Date(token.expires_at).toLocaleDateString() : <span title={t('adminTokens.expiresHint')}>∞</span>}</td>
                    <td className="small text-muted">{new Date(token.created_at).toLocaleDateString()}</td>
                    <td>{canRevoke && <button type="button" className="btn btn-sm btn-outline-danger" disabled={revoking === token.id} onClick={() => handleRevoke(token.id)}><ShieldOff className="icon" aria-hidden="true" />{t('adminTokens.revoke')}</button>}</td>
                  </tr>;
                })}</tbody>
              </table></div>}
        </Surface>
      </div>
    </div>
  );
}
